/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild, DestroyRef, inject } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { UnespCoreMessageService } from 'src/libs/unesp-core'
import {
  getAndamentoByDescription,
  TipoAndamento,
  TipoAndamentoDetalhe,
  TipoAndamentoDetalhes,
  getAndamentosPorTipoConcurso,
} from 'src/app/enums/tipo-andamento'
import { Andamento } from 'src/app/models/andamento'
import { AndamentoService } from 'src/app/services/andamento.service'
import { PermitirAlteracaoService } from 'src/app/services/permitir-alteracao.service'
import {
  ArquivoSelecionado,
  ArquivoStatus,
  ArquivoUploadComponent,
  arquivoUploadValidator,
} from '../../arquivo-upload/arquivo-upload.component'
import { DatePipe } from '@angular/common'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-andamento-form',
  templateUrl: './andamento-form.component.html',
  styleUrls: ['./andamento-form.component.css'],
})
export class AndamentoFormComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef)

  datePipe: DatePipe = new DatePipe('pt-BR')

  @ViewChild('upload') upload: ArquivoUploadComponent | undefined

  @Input() isPermitirAlteracao: boolean = false
  @Input() andamento?: Andamento
  @Input() idConcurso?: number
  @Input() tipoConcurso?: string
  @Input() valorInscricao?: number
  @Input() possuiRecursoDeferidoResultadoFinal: boolean = false

  @Output() andamentoUpdate = new EventEmitter<Andamento | undefined>()

  id?: number
  isAndamentoConcluido: boolean = false
  exibirAlertaRetificacao: boolean = false

  tipoAndamento = TipoAndamento
  tipoAndamentoDetalhe?: TipoAndamentoDetalhe
  andamentosPermitidos: { [key: string]: string } = {}

  andamentoUploadPath: string = 'andamento/'
  andamentoUploadFc: FormControl<ArquivoSelecionado>

  form = new FormGroup({
    tipo: new FormControl('', Validators.required),
    titulo: new FormControl('', Validators.required),
    complemento: new FormControl(''),
    publicacao: new FormControl(new Date()),
    pagina: new FormControl(''),
    sessao: new FormControl(''),
    recursoInicio: new FormControl(new Date()),
    recursoFim: new FormControl(new Date()),
    anexo: new FormControl(''),
    notificarApenasPendentes: new FormControl(false),
  })

  constructor(
    private andamentoService: AndamentoService,
    private permitirAlteracaoService: PermitirAlteracaoService,
    private unespCoreMessageService: UnespCoreMessageService
  ) {
    this.andamentoUploadFc = new FormControl(new ArquivoSelecionado(), {
      validators: [arquivoUploadValidator(false)],
      updateOn: 'submit',
    }) as FormControl<ArquivoSelecionado>
  }

  ngOnInit(): void {
    if (this.isPermitirAlteracao) this.form.disable()
    this.carregarAndamentosPermitidos()
  }

  ngOnChanges() {
    this.carregarAndamentosPermitidos()
    this.carregarAndamento(this.andamento)
  }

  carregarAndamento(andamento: Andamento | undefined): void {
    this.form.reset()
    if (typeof andamento === 'undefined') {
      this.id = undefined
      this.andamentoUploadFc.setValue(new ArquivoSelecionado())
    } else {
      this.form.controls['tipo'].setValue(getAndamentoByDescription(andamento.tipo))
      this.selecionaTipo()
      let publicacao =
        andamento.publicacao == null ? null : andamento.publicacao
      let recursoInicio =
        andamento.recursoInicio == null
          ? null
          : andamento.recursoInicio
      let recursoFim =
        andamento.recursoFim == null ? null : andamento.recursoFim
      this.form.controls['titulo'].setValue(andamento.titulo)
      this.form.controls['complemento'].setValue(andamento.complemento)
      this.form.controls['publicacao'].setValue(publicacao as any)
      this.form.controls['pagina'].setValue(andamento.pagina)
      this.form.controls['sessao'].setValue(andamento.sessao)
      this.form.controls['recursoInicio'].setValue(recursoInicio as any)
      this.form.controls['recursoFim'].setValue(recursoFim as any)
      this.form.controls['notificarApenasPendentes'].setValue(andamento.notificarApenasPendentes ?? false)
      this.id = Number(andamento.id!)
      this.isAndamentoConcluido = andamento.andamentoConcluido as boolean
      let anexo = this.andamentoUploadFc.value
      anexo.setNameAndSize(andamento.anexo, andamento.anexoTamanho as number)
      this.andamentoUploadFc.setValue(anexo)
      this.andamentoUploadPath = 'andamento/' + andamento.id
    }
  }

  async gravar() {
    const arrNameErros = this.applyTouchedToInvalidFields()

    if (!this.form.valid) {
      this.unespCoreMessageService.showMessageError(`Erro ao preencher os dados do formulário nos campos: ${arrNameErros.join(', ')}`)
      return
    }

    // Validar anexo obrigatório
    if (this.tipoAndamentoDetalhe?.anexoObrigatorio) {
      const anexo = this.andamentoUploadFc.value
      if (anexo.status === ArquivoStatus.VAZIO) {
        this.unespCoreMessageService.showMessageError(
          `O tipo de andamento '${this.tipoAndamento[this.form.get('tipo')?.value as keyof typeof TipoAndamento]}' exige anexo obrigatório`
        )
        return
      }
    }

    let formulario = <Andamento>this.form.value

    if (this.id) {
      this.andamentoService.atualizar(this.idConcurso!, this.id, formulario).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (andamento) => {
          this.unespCoreMessageService.showMessageSuccess(`Andamento #${andamento.id} atualizado com sucesso`)
          this.anexoUpload(andamento.id, andamento)
        },
        error: (err) => {
          this.unespCoreMessageService.showMessageError(err.error?.message || 'Erro ao atualizar andamento')
        }
      })
    } else {
      this.andamentoService.gravar(this.idConcurso!, formulario).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (andamento) => {
          this.unespCoreMessageService.showMessageSuccess(`Andamento #${andamento.id} cadastrado com sucesso`)
          this.anexoUpload(andamento.id, andamento)
        },
        error: (err) => {
          this.unespCoreMessageService.showMessageError(err.error?.message || 'Erro ao cadastrar andamento')
        }
      })
    }
  }

  private anexoUpload(id: number | string | undefined, andamento: Andamento) {
    if (this.andamentoUploadFc.value.status != ArquivoStatus.SELECIONADO) {
      if (this.tipoAndamentoDetalhe?.anexoObrigatorio) {
        this.andamentoService.validarAnexoObrigatorio(this.idConcurso!, Number(id)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.unespCoreMessageService.showMessageError('Anexo obrigatório não foi enviado')
            this.andamentoUpdate.emit(undefined)
          },
          error: (err) => {
            this.unespCoreMessageService.showMessageError(err.error?.message || 'Erro ao validar anexo')
          }
        })
        return
      }
      this.andamentoUpdate.emit(andamento)
      return
    }

    if (typeof id == 'undefined') return

    this.upload!.uploadPath = 'andamento/' + id
    const p = this.upload?.upload()
    if (p != null)
      p.then(arqStatus => {
        andamento.anexo = arqStatus.uuid + '.pdf'
        andamento.anexoTamanho = arqStatus.size
        this.andamentoUpdate.emit(andamento)
      }).catch(() => {
        if (this.tipoAndamentoDetalhe?.anexoObrigatorio) {
          this.andamentoService.validarAnexoObrigatorio(this.idConcurso!, Number(id)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: () => {
              this.unespCoreMessageService.showMessageError('Falha no upload do anexo obrigatório')
              this.andamentoUpdate.emit(undefined)
            },
            error: (err) => {
              this.unespCoreMessageService.showMessageError(err.error?.message || 'Erro ao validar anexo após falha no upload')
            }
          })
        }
      })
  }

  applyTouchedToInvalidFields() {
    const arrNameErrors = []
    for (const name in this.form.controls) {
      if (this.form.get(name)?.invalid) {
        this.form.get(name)?.markAsTouched()
        arrNameErrors.push(name)
      }
    }
    return arrNameErrors
  }

  cancelar(): void {
    this.form.reset()
    this.id = undefined
    this.andamentoUpdate.emit(undefined)
  }

  permitirAlteracao() {
    if (this.id && this.isPermitirAlteracao) {
      this.permitirAlteracaoService.liberarRegistro('andamento', this.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(registro => {
        this.isAndamentoConcluido = registro.andamento.concluido as boolean
        this.unespCoreMessageService.showMessageSuccess(`Andamento #${this.id} revertido para não concluído`)
      })
    }
  }

  clearValidations(name: string): void {
    this.form.get(name)?.reset()
    this.form.get(name)?.clearValidators()
    this.form.get(name)?.updateValueAndValidity()
  }

  private skipWeekend(date: Date): Date {
    if (date.getDay() === 6) {
      date.setDate(date.getDate() + 2)
      return date
    } else if (date.getDay() === 0) {
      date.setDate(date.getDate() + 1)
      return date
    } else {
      return date
    }
  }

  selecionaTipo(): void {
    const tipo = this.form.get('tipo')?.getRawValue()

    this.exibirAlertaRetificacao = this.possuiRecursoDeferidoResultadoFinal && (tipo === 'RESULTADO_RECURSO' || tipo === 'RESULTADO_FINAL_DEFINITIVO')
    this.tipoAndamentoDetalhe = TipoAndamentoDetalhes.get(tipo)

    this.clearValidations('recursoInicio')
    this.clearValidations('recursoFim')
    this.clearValidations('publicacao')
    this.clearValidations('pagina')
    this.clearValidations('sessao')

    // Atualizar validador do anexo
    this.andamentoUploadFc.setValidators([arquivoUploadValidator(this.tipoAndamentoDetalhe?.anexoObrigatorio ?? false)])
    this.andamentoUploadFc.updateValueAndValidity()

    let hoje = new Date()
    if (this.tipoAndamentoDetalhe?.recurso && this.andamento) {
      let recursoInicio = this.andamento.recursoInicio != null
        ? this.andamento.recursoInicio
        : null
      let recursoFim = this.andamento.recursoFim != null
        ? this.andamento.recursoFim
        : null

      this.form.controls['recursoInicio'].setValue(recursoInicio as any)
      this.form.controls['recursoFim'].setValue(recursoFim as any)

      this.form.controls['recursoInicio'].updateValueAndValidity()
      this.form.controls['recursoFim'].updateValueAndValidity()
    }
    if (this.tipoAndamentoDetalhe?.doe) {
      if (this.andamento) {
        let publicacao = this.andamento.publicacao != null
          ? this.andamento.publicacao
          : null
        this.form.controls['publicacao'].setValue(publicacao as any)
      }

      this.form.controls['publicacao'].updateValueAndValidity()
      this.form.controls['pagina'].updateValueAndValidity()
      this.form.controls['sessao'].updateValueAndValidity()
    }
  }

  clearDate(field: string): void {
    if (field === 'publicacao' || field === 'recursoInicio' || field === 'recursoFim') {
      this.form.controls[field].setValue(null)
    }
  }

  private carregarAndamentosPermitidos(): void {
    if (this.tipoConcurso) {
      const andamentosKeys = getAndamentosPorTipoConcurso(this.tipoConcurso)
      this.andamentosPermitidos = {}
      andamentosKeys.forEach(key => {
        if (TipoAndamento[key as keyof typeof TipoAndamento]) {
          this.andamentosPermitidos[key] = TipoAndamento[key as keyof typeof TipoAndamento]
        }
      })
    } else {
      this.andamentosPermitidos = { ...TipoAndamento }
    }

    // Remove REDUCAO se valorInscricao for 0
    if (this.valorInscricao === 0 && this.andamentosPermitidos['REDUCAO']) {
      delete this.andamentosPermitidos['REDUCAO']
    }

    // RESULTADO_FASE_2 descontinuado — ocultar da criação de novos andamentos
    delete this.andamentosPermitidos['RESULTADO_FASE_2']
  }
}
